import React, { useEffect, useMemo, useRef, useState } from "react";
import {
    Badge,
    Button,
    Form,
    Modal,
    OverlayTrigger,
    Tooltip,
} from "react-bootstrap";
import {
    EMBED_DISPLAY_MODE_FLOATING,
    EMBED_DISPLAY_MODE_INLINE,
} from "../../../embedTicketFormTheme";
import { EmbedFormBtn } from "./embedFormButtons";

const COPIED_RESET_MS = 2000;
const EMBED_FORM_NAME_DISPLAY_MAX = 48;

function formatEmbedFormNameForDisplay(name) {
    const text = String(name ?? "").trim();
    if (text.length <= EMBED_FORM_NAME_DISPLAY_MAX) {
        return { display: text, full: text, truncated: false };
    }
    return {
        display: `${text.slice(0, EMBED_FORM_NAME_DISPLAY_MAX).trimEnd()}...`,
        full: text,
        truncated: true,
    };
}

function EmbedHowToFormName({ name }) {
    const text = String(name ?? "").trim();
    if (!text) {
        return null;
    }
    const { display, full, truncated } = formatEmbedFormNameForDisplay(text);
    const label = <strong className="embed-howto-form-name">{display}</strong>;
    if (!truncated) {
        return label;
    }
    return (
        <OverlayTrigger
            placement="top"
            overlay={<Tooltip id="embed-howto-form-name">{full}</Tooltip>}
        >
            <span className="embed-howto-form-name-wrap">{label}</span>
        </OverlayTrigger>
    );
}

/** @returns {Promise<boolean>} whether text was written via the Clipboard API */
export async function copyEmbedSnippetToClipboard(text) {
    try {
        await navigator.clipboard.writeText(text);
        return true;
    } catch (e) {
        window.prompt("Copy:", text);
        return false;
    }
}

const PLACEHOLDER_UUID = "YOUR_WIDGET_UUID";

function embedHowToCopyButtonProps(copiedOption, copyKey) {
    const isCopied = copiedOption === copyKey;
    return {
        type: "button",
        variant: EmbedFormBtn.secondary,
        size: "sm",
        className: `embed-howto-copy-btn${isCopied ? " is-copied" : ""}`,
    };
}

function EmbedHowToModeTabs({ value, onChange }) {
    return (
        <div
            className="embed-howto-mode-tabs embed-forms-segment-toggle d-flex flex-nowrap gap-2"
            role="group"
            aria-label="Embed display type"
        >
            <Button
                type="button"
                variant={
                    value === EMBED_DISPLAY_MODE_INLINE
                        ? EmbedFormBtn.primary
                        : EmbedFormBtn.secondary
                }
                className="embed-forms-segment-toggle__btn"
                onClick={() => onChange(EMBED_DISPLAY_MODE_INLINE)}
                aria-pressed={value === EMBED_DISPLAY_MODE_INLINE}
            >
                Inline
            </Button>
            <Button
                type="button"
                variant={
                    value === EMBED_DISPLAY_MODE_FLOATING
                        ? EmbedFormBtn.primary
                        : EmbedFormBtn.secondary
                }
                className="embed-forms-segment-toggle__btn"
                onClick={() => onChange(EMBED_DISPLAY_MODE_FLOATING)}
                aria-pressed={value === EMBED_DISPLAY_MODE_FLOATING}
            >
                Floating
            </Button>
        </div>
    );
}

function EmbedHowToCodeSample({ children }) {
    return (
        <pre className="embed-howto-code-sample mb-0">
            <code>{children}</code>
        </pre>
    );
}

function EmbedHowToStep({ children, code }) {
    return (
        <div className="embed-howto-step">
            <p className="embed-howto-step__text mb-2">{children}</p>
            {code}
        </div>
    );
}

function EmbedHowToInlineInstructions({
    copiedOption,
    onCopy,
    codeOptionAScript,
    codeOptionAMount,
    codeOptionB,
    showCopyButtons = true,
}) {
    if (!showCopyButtons) {
        return (
            <>
                <p className="text-muted mb-3">
                    The form is embedded directly in your page layout — place the
                    mount point wherever you want it to appear.
                </p>
                <div className="embed-howto-option mb-4">
                    <h6 className="fw-semibold d-flex align-items-center gap-2 mb-2">
                        <span>
                            Option A — script + mount{" "}
                            <code>&lt;div&gt;</code>
                        </span>
                        <Badge bg="success" pill className="text-uppercase">
                            Recommended
                        </Badge>
                    </h6>
                    <EmbedHowToStep
                        code={
                            <EmbedHowToCodeSample>
                                {codeOptionAScript}
                            </EmbedHowToCodeSample>
                        }
                    >
                        <span className="embed-howto-step__label">1.</span> Add the
                        embed script inside your page&rsquo;s{" "}
                        <code className="text-nowrap">&lt;head&gt;</code>:
                    </EmbedHowToStep>
                    <EmbedHowToStep
                        code={
                            <EmbedHowToCodeSample>
                                {codeOptionAMount}
                            </EmbedHowToCodeSample>
                        }
                    >
                        <span className="embed-howto-step__label">2.</span> Place a mount{" "}
                        <code className="text-nowrap">&lt;div&gt;</code> anywhere inside{" "}
                        <code className="text-nowrap">&lt;body&gt;</code> where the form
                        should appear. Replace <code>{PLACEHOLDER_UUID}</code> with your
                        form&rsquo;s widget ID:
                    </EmbedHowToStep>
                </div>
                <div className="embed-howto-option mb-0">
                    <h6 className="fw-semibold mb-2">Option B — one tag</h6>
                    <p className="text-muted mb-2">
                        Alternatively, paste a single script tag where the widget should
                        appear inside <code className="text-nowrap">&lt;body&gt;</code>.
                        The script loads the form next to itself.
                    </p>
                    <EmbedHowToCodeSample>{codeOptionB}</EmbedHowToCodeSample>
                </div>
            </>
        );
    }

    return (
        <>
            <p className="text-muted mb-3">
                The form is embedded directly in your page layout — place the
                mount point wherever you want it to appear.
            </p>
            <div className="embed-howto-option mb-4">
                <h6 className="fw-semibold d-flex align-items-center gap-2 mb-2">
                    <span>
                        Option A — script + mount{" "}
                        <code>&lt;div&gt;</code>
                    </span>
                    <Badge bg="success" pill className="text-uppercase">
                        Recommended
                    </Badge>
                </h6>
                <p className="text-muted mb-3">
                    Add the script inside your page&rsquo;s{" "}
                    <code className="text-nowrap">&lt;head&gt;</code>, then
                    place the mount{" "}
                    <code className="text-nowrap">&lt;div&gt;</code> anywhere
                    inside the{" "}
                    <code className="text-nowrap">&lt;body&gt;</code> where you
                    want the form to appear.
                </p>
                <div className="embed-howto-option__block">
                    <div className="d-flex align-items-center justify-content-between mb-1 gap-2 flex-wrap">
                        <span className="text-muted small">
                            1. Inside{" "}
                            <code className="text-nowrap">&lt;head&gt;</code>
                        </span>
                        <Button
                            {...embedHowToCopyButtonProps(
                                copiedOption,
                                "A-script"
                            )}
                            onClick={() =>
                                onCopy("A-script", codeOptionAScript)
                            }
                        >
                            {copiedOption === "A-script"
                                ? "Copied"
                                : "Copy script"}
                        </Button>
                    </div>
                    <Form.Control
                        as="textarea"
                        readOnly
                        rows={2}
                        className="font-monospace small mb-3"
                        value={codeOptionAScript}
                    />
                </div>
                <div className="embed-howto-option__block">
                    <div className="d-flex align-items-center justify-content-between mb-1 gap-2 flex-wrap">
                        <span className="text-muted small">
                            2. Anywhere inside{" "}
                            <code className="text-nowrap">&lt;body&gt;</code>
                        </span>
                        <Button
                            {...embedHowToCopyButtonProps(
                                copiedOption,
                                "A-mount"
                            )}
                            onClick={() =>
                                onCopy("A-mount", codeOptionAMount)
                            }
                        >
                            {copiedOption === "A-mount"
                                ? "Copied"
                                : "Copy mount div"}
                        </Button>
                    </div>
                    <Form.Control
                        as="textarea"
                        readOnly
                        rows={2}
                        className="font-monospace small mb-0"
                        value={codeOptionAMount}
                    />
                </div>
            </div>
            <div className="embed-howto-option mb-0">
                <h6 className="fw-semibold mb-2">Option B — one tag</h6>
                <p className="text-muted mb-2">
                    Place this single tag where the widget should appear inside
                    your <code className="text-nowrap">&lt;body&gt;</code>. The
                    script loads the form next to itself.
                </p>
                <div className="d-flex align-items-center justify-content-between mb-1 gap-2 flex-wrap">
                    <span className="text-muted small">
                        Inside <code className="text-nowrap">&lt;body&gt;</code>
                    </span>
                    <Button
                        {...embedHowToCopyButtonProps(copiedOption, "B")}
                        onClick={() => onCopy("B", codeOptionB)}
                    >
                        {copiedOption === "B" ? "Copied" : "Copy option B"}
                    </Button>
                </div>
                <Form.Control
                    as="textarea"
                    readOnly
                    rows={3}
                    className="font-monospace small mb-0"
                    value={codeOptionB}
                />
            </div>
        </>
    );
}

function EmbedHowToFloatingInstructions({
    copiedOption,
    onCopy,
    codeOptionB,
    showCopyButtons = true,
}) {
    if (!showCopyButtons) {
        return (
            <>
                <p className="text-muted mb-3">
                    A round launcher button stays fixed in the corner you chose in
                    the editor. Visitors click it to open the form in a popup — no
                    mount <code>&lt;div&gt;</code> is required.
                </p>
                <div className="embed-howto-option mb-0">
                    <h6 className="fw-semibold mb-2">
                        Floating button — one script tag
                    </h6>
                    <p className="text-muted mb-2">
                        Paste this script inside{" "}
                        <code className="text-nowrap">&lt;head&gt;</code> or before{" "}
                        <code className="text-nowrap">&lt;/body&gt;</code>. Replace{" "}
                        <code>{PLACEHOLDER_UUID}</code> with your form&rsquo;s widget ID.
                        The launcher uses your form button color and opens the panel in the
                        configured corner.
                    </p>
                    <EmbedHowToCodeSample>{codeOptionB}</EmbedHowToCodeSample>
                </div>
            </>
        );
    }

    return (
        <>
            <p className="text-muted mb-3">
                A round launcher button stays fixed in the corner you chose in
                the editor. Visitors click it to open the form in a popup — no
                mount <code>&lt;div&gt;</code> is required.
            </p>
            <div className="embed-howto-option mb-0">
                <h6 className="fw-semibold mb-2">
                    Floating button — one script tag
                </h6>
                <p className="text-muted mb-2">
                    Paste inside <code>&lt;head&gt;</code> or before{" "}
                    <code>&lt;/body&gt;</code>. The launcher uses your form
                    button color and opens the panel in the configured corner.
                </p>
                <div className="d-flex align-items-center justify-content-between mb-1 gap-2 flex-wrap">
                    <span className="text-muted small">Embed script</span>
                    <Button
                        {...embedHowToCopyButtonProps(copiedOption, "float")}
                        onClick={() => onCopy("float", codeOptionB)}
                    >
                        {copiedOption === "float" ? "Copied" : "Copy"}
                    </Button>
                </div>
                <Form.Control
                    as="textarea"
                    readOnly
                    rows={2}
                    className="font-monospace small mb-0"
                    value={codeOptionB}
                />
            </div>
        </>
    );
}

/**
 * @param {object} props
 * @param {boolean} props.show
 * @param {() => void} props.onHide
 * @param {'generic'|'postCreate'} props.variant
 * @param {string} [props.formName]
 * @param {string|null} [props.uuid] real UUID when variant is postCreate
 * @param {number|null} [props.formId] for Continue editing
 * @param {() => void} [props.onContinueEditing]
 * @param {() => void} [props.onGoToList]
 * @param {string} [props.title]
 * @param {'inline'|'floating'} [props.displayMode] form display mode — locks embed instructions when copying a specific form
 */
export default function EmbedHowToModal({
    show,
    onHide,
    variant = "generic",
    formName = "",
    uuid = null,
    formId = null,
    displayMode = EMBED_DISPLAY_MODE_INLINE,
    onContinueEditing,
    onGoToList,
    title,
}) {
    const configuredMode =
        displayMode === EMBED_DISPLAY_MODE_FLOATING
            ? EMBED_DISPLAY_MODE_FLOATING
            : EMBED_DISPLAY_MODE_INLINE;

    const scriptUrl = useMemo(
        () => `${window.location.origin}/js/embed-ticket-form.js`,
        []
    );

    const [modeTab, setModeTab] = useState(configuredMode);
    const [copiedOption, setCopiedOption] = useState(null);
    const copiedTimerRef = useRef(null);

    useEffect(() => {
        return () => {
            if (copiedTimerRef.current) {
                clearTimeout(copiedTimerRef.current);
            }
        };
    }, []);

    useEffect(() => {
        if (!show) {
            setCopiedOption(null);
            if (copiedTimerRef.current) {
                clearTimeout(copiedTimerRef.current);
                copiedTimerRef.current = null;
            }
            return;
        }
        setModeTab(configuredMode);
    }, [show, configuredMode]);

    const hasRealUuid = !!(uuid && String(uuid).trim());
    const isTutorial = variant === "generic" && !hasRealUuid;
    const showCopyButtons = !isTutorial;
    const showModeTabs = !hasRealUuid;
    const widgetUuid = hasRealUuid ? String(uuid).trim() : PLACEHOLDER_UUID;
    const activeMode = showModeTabs ? modeTab : configuredMode;
    const configuredModeLabel =
        configuredMode === EMBED_DISPLAY_MODE_FLOATING
            ? "Floating"
            : "Inline";

    const codeOptionAScript = `<script src="${scriptUrl}" defer></script>`;
    const codeOptionAMount = `<div data-claritick-ticket-embed="${widgetUuid}"></div>`;
    const codeOptionB = `<script src="${scriptUrl}" data-widget="${widgetUuid}" defer></script>`;

    async function handleCopyOption(key, text) {
        const ok = await copyEmbedSnippetToClipboard(text);
        if (!ok) {
            return;
        }
        setCopiedOption(key);
        if (copiedTimerRef.current) {
            clearTimeout(copiedTimerRef.current);
        }
        copiedTimerRef.current = setTimeout(() => {
            copiedTimerRef.current = null;
            setCopiedOption(null);
        }, COPIED_RESET_MS);
    }

    const modalTitle =
        title ||
        (variant === "postCreate"
            ? "Embed this form"
            : hasRealUuid
              ? "Embed code"
              : "How to embed");

    return (
        <Modal show={show} onHide={onHide} size="lg" centered className="embed-forms-ui">
            <Modal.Header closeButton>
                <Modal.Title>{modalTitle}</Modal.Title>
            </Modal.Header>
            <Modal.Body className="embed-howto-modal-body small">
                {variant === "postCreate" ? (
                    <p className="embed-howto-intro mb-3">
                        <EmbedHowToFormName name={formName} /> was saved. This form uses
                        an <strong>{configuredModeLabel}</strong> embed — paste
                        the snippet below into your site. The page must be
                        served from a <strong>trusted domain</strong> you added
                        for this form (hostname only, e.g.{" "}
                        <code>example.com</code>).
                    </p>
                ) : hasRealUuid ? (
                    <p className="embed-howto-intro mb-3">
                        Paste the snippet below into your site.
                        {formName ? (
                            <>
                                {" "}
                                This <strong>{configuredModeLabel}</strong>{" "}
                                widget is wired to{" "}
                                <EmbedHowToFormName name={formName} />.
                            </>
                        ) : (
                            <>
                                {" "}
                                This form uses an{" "}
                                <strong>{configuredModeLabel}</strong> embed.
                            </>
                        )}{" "}
                        The page must be served from a{" "}
                        <strong>trusted domain</strong> you added for this form
                        (hostname only, e.g. <code>example.com</code>).
                    </p>
                ) : (
                    <p className="embed-howto-intro mb-3">
                        Follow the steps below to add a Claritick embed form to your
                        website. Your site must be on a{" "}
                        <strong>trusted domain</strong> (manage hostnames under{" "}
                        <strong>Trusted Domains</strong>). When you are ready to embed a
                        specific form, use <strong>Get embed code</strong> on that row for
                        copy-ready snippets with your widget ID.
                    </p>
                )}

                {showModeTabs ? (
                    <EmbedHowToModeTabs value={modeTab} onChange={setModeTab} />
                ) : null}

                {activeMode === EMBED_DISPLAY_MODE_FLOATING ? (
                    <EmbedHowToFloatingInstructions
                        copiedOption={copiedOption}
                        onCopy={handleCopyOption}
                        codeOptionB={codeOptionB}
                        showCopyButtons={showCopyButtons}
                    />
                ) : (
                    <EmbedHowToInlineInstructions
                        copiedOption={copiedOption}
                        onCopy={handleCopyOption}
                        codeOptionAScript={codeOptionAScript}
                        codeOptionAMount={codeOptionAMount}
                        codeOptionB={codeOptionB}
                        showCopyButtons={showCopyButtons}
                    />
                )}
            </Modal.Body>
            {variant === "postCreate" && onContinueEditing && onGoToList ? (
                <Modal.Footer className="flex-wrap gap-2">
                    <Button
                        type="button"
                        variant={EmbedFormBtn.secondary}
                        onClick={onContinueEditing}
                        disabled={!formId}
                    >
                        Continue editing
                    </Button>
                    <Button
                        type="button"
                        variant={EmbedFormBtn.primary}
                        onClick={onGoToList}
                    >
                        Go to forms list
                    </Button>
                </Modal.Footer>
            ) : (
                <Modal.Footer>
                    <Button type="button" variant={EmbedFormBtn.primary} onClick={onHide}>
                        Close
                    </Button>
                </Modal.Footer>
            )}
        </Modal>
    );
}
